import {
  Box,
  Container,
  Typography,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  Gavel as GavelIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  Block as BlockIcon,
  Copyright as CopyrightIcon,
  Security as SecurityIcon,
  Cancel as CancelIcon,
  Assignment as AssignmentIcon,
  ContactSupport as ContactSupportIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

const TermsOfUsePage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const sections = [
    {
      id: 'acceptance',
      title: '1. Aceitação dos Termos',
      icon: GavelIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Bem-vindo ao <strong>Hostly</strong>! Ao baixar, acessar ou utilizar nosso aplicativo móvel ("App") 
            ou website, você concorda em ficar vinculado a estes Termos de Uso ("Termos").
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Se você não concordar com estes Termos, não deverá utilizar o Hostly. Estes Termos constituem um 
            acordo legal vinculativo entre você e o Hostly.
          </Typography>
          <Typography variant="body1">
            Ao utilizar o App, você também concorda com nossa <Link onClick={() => navigate('/privacy-policy')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>Política de Privacidade</Link>, 
            que descreve como coletamos e utilizamos suas informações.
          </Typography>
        </>
      ),
    },
    {
      id: 'subscriptions',
      title: '2. Assinaturas e Pagamentos',
      icon: PaymentIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            O Hostly oferece planos de assinatura ("Hostly Basic" e "Hostly Pro") que desbloqueiam recursos premium.
          </Typography>
          
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>
            2.1. Assinaturas Auto-renováveis
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText 
                primary="Ciclos de Cobrança"
                secondary="As assinaturas estão disponíveis em planos mensais ou anuais."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText 
                primary="Renovação Automática"
                secondary="Sua assinatura será renovada automaticamente, a menos que o cancelamento seja feito pelo menos 24 horas antes do final do período atual."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText 
                primary="Cobrança"
                secondary="O pagamento será cobrado em sua conta do iTunes (iOS) ou método de pagamento cadastrado (Android/Web) na confirmação da compra."
              />
            </ListItem>
          </List>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            2.2. Cancelamento e Reembolso
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Você pode gerenciar e cancelar suas assinaturas acessando as configurações da sua conta na App Store (iOS) 
            ou Google Play Store (Android) após a compra.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>
            O cancelamento da assinatura atual não gera reembolso do período já pago, mas o acesso continuará 
            ativo até o final do ciclo de cobrança vigente.
          </Typography>
          
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
            2.3. Alterações de Preço
          </Typography>
          <Typography variant="body2">
            Reservamo-nos o direito de ajustar os preços de nossos serviços. Qualquer alteração de preço na sua 
            assinatura será comunicada com antecedência e, se aplicável, exigirá seu consentimento para renovação.
          </Typography>
        </>
      ),
    },
    {
      id: 'usage',
      title: '3. Uso do Aplicativo e Conteúdo',
      icon: AssignmentIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            O Hostly permite que você organize eventos, gerencie acervos e compartilhe receitas.
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText 
                primary="Uso Pessoal"
                secondary="O serviço é destinado ao uso pessoal e não comercial, salvo autorização expressa."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText 
                primary="Responsabilidade pelo Conteúdo"
                secondary="Você é o único responsável por todas as fotos, receitas e dados que carrega no aplicativo."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon sx={{ color: theme.colors.primary }} /></ListItemIcon>
              <ListItemText 
                primary="Backup"
                secondary="Embora realizemos backups regulares (Planos Premium/Pro), recomendamos que mantenha cópias de seus dados importantes."
              />
            </ListItem>
          </List>
        </>
      ),
    },
    {
      id: 'community',
      title: '4. Regras da Comunidade (HostlyClub)',
      icon: SecurityIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Ao utilizar recursos sociais como o HostlyClub para compartilhar receitas ou eventos, você concorda em:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><BlockIcon sx={{ color: '#d32f2f' }} /></ListItemIcon>
              <ListItemText 
                primary="Não publicar conteúdo ofensivo"
                secondary="É proibido conteúdo que seja ilegal, abusivo, difamatório, pornográfico ou que incite ódio."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><BlockIcon sx={{ color: '#d32f2f' }} /></ListItemIcon>
              <ListItemText 
                primary="Não violar direitos autorais"
                secondary="Compartilhe apenas fotos e receitas que você tem o direito de distribuir."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><BlockIcon sx={{ color: '#d32f2f' }} /></ListItemIcon>
              <ListItemText 
                primary="Não realizar spam"
                secondary="Não utilize a plataforma para publicidade não autorizada ou solicitações comerciais."
              />
            </ListItem>
          </List>
          <Typography variant="body2" sx={{ mt: 2 }}>
            O Hostly reserva-se o direito de remover qualquer conteúdo que viole estes termos e de suspender 
            contas de usuários que desrespeitem estas regras.
          </Typography>
        </>
      ),
    },
    {
      id: 'intellectual-property',
      title: '5. Propriedade Intelectual',
      icon: CopyrightIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            O design do aplicativo, logotipos (Hostly), textos, gráficos e software são propriedade exclusiva 
            do Hostly e são protegidos por leis de direitos autorais e propriedade intelectual.
          </Typography>
          <Typography variant="body1">
            Seu conteúdo (receitas, fotos de eventos) permanece sua propriedade. Ao postar no HostlyClub, 
            você concede ao Hostly uma licença não exclusiva para exibir esse conteúdo na plataforma.
          </Typography>
        </>
      ),
    },
    {
      id: 'disclaimer',
      title: '6. Isenção de Garantias',
      icon: DescriptionIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            O serviço é fornecido "como está" e "conforme disponível". O Hostly não garante que o serviço 
            será ininterrupto, livre de erros ou que atenderá a todos os seus requisitos específicos.
          </Typography>
          <Typography variant="body1">
            Não nos responsabilizamos por perdas de dados decorrentes de falhas no dispositivo, conectividade 
            ou uso indevido do aplicativo.
          </Typography>
        </>
      ),
    },
    {
      id: 'termination',
      title: '7. Encerramento',
      icon: CancelIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Podemos encerrar ou suspender seu acesso imediatamente, sem aviso prévio ou responsabilidade, 
            por qualquer motivo, inclusive se você violar estes Termos.
          </Typography>
          <Typography variant="body1">
            Após o encerramento, seu direito de usar o Serviço cessará imediatamente.
          </Typography>
        </>
      ),
    },
    {
      id: 'contact',
      title: '8. Contato',
      icon: ContactSupportIcon,
      content: (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Se você tiver alguma dúvida sobre estes Termos, entre em contato conosco:
          </Typography>
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#F5F5F5', borderRadius: theme.radii.medium }}>
             <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Hostly</strong>
            </Typography>
            <Typography variant="body2">
              <strong>E-mail:</strong> suporte@hostly.app
            </Typography>
          </Paper>
        </>
      ),
    },
  ];

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      pb: 4,
    }}>
      {/* Header fixo com logo */}
      <Box sx={{ 
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        backgroundColor: theme.colors.primary,
        borderBottomLeftRadius: 28,
        borderBottomRightRadius: 28,
        boxShadow: 3,
        overflow: 'hidden',
      }}>
        {/* Flores decorativas */}
        <img
          src="/hostly-02.png"
          alt="Logo decorativa"
          style={{ 
            position: "absolute", 
            top: -20,
            left: -60,
            width: 180,
            opacity: 0.3,
            transform: "rotate(15deg)"
          }}
        />
        
        <img
          src="/hostly-02.png"
          alt="Logo decorativa"
          style={{ 
            position: "absolute", 
            bottom: -10,
            right: -60,
            width: 180,
            opacity: 0.2
          }}
        />

        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            py: 3,
            gap: 2,
            position: 'relative',
            zIndex: 2,
          }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            
            {/* Logo central */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
              <img 
                src="/hostly_Prancheta 1.png" 
                alt="Hostly Logo" 
                style={{ 
                  height: "60px",
                  width: "auto",
                }} 
              />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                  Termos de Uso
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Última atualização: 15 de Dezembro de 2025
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Conteúdo */}
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Resumo em destaque */}
        <Paper 
          elevation={2}
          sx={{ 
            p: 3,
            mb: 4,
            backgroundColor: theme.colors.secondary,
            color: 'white',
            borderRadius: theme.radii.medium,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <DescriptionIcon sx={{ fontSize: 40 }} />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Contrato de Licença de Usuário Final
            </Typography>
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Estes termos regulam o uso do aplicativo Hostly e seus serviços de assinatura. 
            Leia atentamente antes de prosseguir.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip label="EULA" sx={{ backgroundColor: 'white', fontWeight: 600 }} />
            <Chip label="Assinaturas Auto-renováveis" sx={{ backgroundColor: 'white', fontWeight: 600 }} />
          </Box>
        </Paper>

        {/* Accordion de seções */}
        {sections.map((section) => (
          <Accordion 
            key={section.id}
            defaultExpanded={section.id === 'acceptance' || section.id === 'subscriptions'}
            sx={{
              mb: 2,
              borderRadius: theme.radii.medium,
              '&:before': {
                display: 'none',
              },
              boxShadow: 1,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                backgroundColor: '#FAFAFA',
                borderRadius: theme.radii.medium,
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <section.icon sx={{ color: theme.colors.primary, fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.colors.text.primary }}>
                  {section.title}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 3 }}>
              {section.content}
            </AccordionDetails>
          </Accordion>
        ))}

        {/* Footer */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 3,
            mt: 4,
            backgroundColor: '#F5F5F5',
            borderRadius: theme.radii.medium,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
            © 2025 Hostly. Todos os direitos reservados.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfUsePage;
